import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { drawHistogram } from "./histogram-renderer"
import { useEditor } from "@/utils/useEditor"
import { Icon } from "@/components/icons/Icon"
import { Button } from "@/components/ui/Button"
import { useAtom, useAtomValue } from "jotai/index"
import { highlightsClippingAtom, shadowsClippingAtom } from "@/store/settings-state"
import clsx from "clsx"
import { isCullingModeAtom } from "@/store/project/projects-state"
import { currentPhotoAtom } from "@/store/photo/photos-state"
import { shouldShowLeftPanelAtom, shouldShowRightPanelAtom } from "@/store/ui-views-state"

export const Histogram = () => {
	const { t } = useTranslation("components")
	const canvasRef = useRef<HTMLCanvasElement | null>(null)
	const editor = useEditor()

	const [highlightsClipping, setHighlightsClipping] = useAtom(highlightsClippingAtom)
	const [shadowsClipping, setShadowClipping] = useAtom(shadowsClippingAtom)
	const showingRightPanel = useAtomValue(shouldShowRightPanelAtom)
	const isShowingLeftPanel = useAtomValue(shouldShowLeftPanelAtom)
	const isCullingMode = useAtomValue(isCullingModeAtom)
	const isShown = (isCullingMode && isShowingLeftPanel) || (!isCullingMode && showingRightPanel)
	const currentPhoto = useAtomValue(currentPhotoAtom)

	const [isHighlightsHit, setHighlightsHit] = useState(false)
	const [isShadowsHit, setShadowsHit] = useState(false)

	const buttonClass =
		"mx-1 flex-col justify-center absolute top-1 size-3.5 bg-base-600 rounded-none bg-opacity-70 hover:bg-base-600 hover:border-base-300 hover:border"

	const getSelectedStyle = (isHit: boolean, isSelected: boolean) =>
		`${isSelected ? "border border-base-100" : "no-border"} ${
			isHit ? "text-base-300" : "text-base-800"
		}`

	const updateHistogram = () => {
		drawHistogram(
			canvasRef.current,
			editor.renderer.histogram.red,
			editor.renderer.histogram.green,
			editor.renderer.histogram.blue,
			editor.renderer.histogram.max,
		)

		setShadowsHit(editor.renderer.histogram.hasShadowsClipping)
		setHighlightsHit(editor.renderer.histogram.hasHighlightsClipping)
	}

	useEffect(() => {
		editor.renderer.histogram.events.subscribe((event) => {
			if (event === "computed-histogram") {
				updateHistogram()
			}
		})
	}, [])

	useEffect(() => {
		if (isShown) {
			updateHistogram()
		}
	}, [isShown])

	return (
		<div className="box-border flex w-full shrink-0 select-none flex-col overflow-hidden">
			{currentPhoto && <canvas ref={canvasRef} className="relative h-[80px] w-full" />}
			{!currentPhoto && <div className="relative h-[60px] w-full"></div>}
			{!isCullingMode && (
				<Button
					trackingLabel="set_shadows_clipping"
					aria-label={t("histogram.ariaToggleShadowsClipping")}
					trackingValue={shadowsClipping ? "true" : "false"}
					variant="custom"
					size="custom"
					isSelected={shadowsClipping}
					onClick={() => {
						setShadowClipping(!shadowsClipping)
					}}
					className={clsx(
						getSelectedStyle(isShadowsHit, shadowsClipping),
						"left-2",
						buttonClass,
					)}
					tooltip={
						shadowsClipping
							? t("histogram.tooltipHideShadowsClipping")
							: t("histogram.tooltipShowShadowsClipping")
					}
				>
					<Icon className="flex size-2 flex-none" name="color_clipping" />
				</Button>
			)}
			{!isCullingMode && (
				<Button
					trackingLabel="set_highlights_clipping"
					aria-label={t("histogram.ariaToggleHighlightsClipping")}
					trackingValue={highlightsClipping ? "true" : "false"}
					variant="custom"
					size="custom"
					isSelected={highlightsClipping}
					onClick={() => {
						setHighlightsClipping(!highlightsClipping)
					}}
					className={clsx(
						getSelectedStyle(isHighlightsHit, highlightsClipping),
						"right-2",
						buttonClass,
					)}
					tooltip={
						highlightsClipping
							? t("histogram.tooltipHideHighlightsClipping")
							: t("histogram.tooltipShowHighlightsClipping")
					}
				>
					<Icon className="flex size-2 flex-none" name="color_clipping" />
				</Button>
			)}
		</div>
	)
}
